import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage, ApiError } from '../../lib/api.js';
import { useAuth, type Session } from '../../lib/auth.js';
import { Button, Input, Card, Spinner } from '../../components/ui/index.js';

function AuthLayout({ title, subtitle, children, footer }: {
  title: string; subtitle?: string; children: ReactNode; footer?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-sky-300/25 blur-3xl" />
      <div className="w-full max-w-md">
        <div className="mb-7 flex items-center justify-center gap-2.5">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-brand-600 to-sky-500 text-lg font-bold text-white shadow-lg shadow-indigo-200">B</span>
          <span className="text-2xl font-bold tracking-tight text-[var(--ink)]">Blogaris</span>
        </div>
        <Card className="border-white/90 p-7 shadow-[0_24px_70px_rgba(79,70,229,.12)] sm:p-8">
          <h1 className="text-xl font-bold text-[var(--ink)]">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-[var(--ink-3)]">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </Card>
        {footer && <div className="mt-5 text-center text-sm text-[var(--ink-3)]">{footer}</div>}
      </div>
    </div>
  );
}

function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
      {error}
    </div>
  );
}

/** รวม logic ที่ทุกฟอร์ม auth ใช้เหมือนกัน: busy, error, error รายฟิลด์ */
function useAuthForm() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<Record<string, string>>({});

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true); setError(null); setFieldError({});
    try {
      await fn();
    } catch (err) {
      if (err instanceof ApiError && err.field) {
        setFieldError({ [err.field]: err.message });
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, fieldError, run, setError };
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuth((s) => s.setSession);
  const { busy, error, run } = useAuthForm();
  const [form, setForm] = useState({ email: '', password: '' });

  return (
    <AuthLayout
      title="เข้าสู่ระบบ"
      subtitle="จัดการบล็อกและ widget ของบริษัทคุณ"
      footer={<>ยังไม่มีบัญชี? <Link to="/register" className="font-medium text-[var(--brand)] hover:underline">สมัครใช้งานฟรี</Link></>}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const session = await api.post<Session>('/v1/auth/login', form, { anonymous: true });
            setSession(session);
            navigate(params.get('next') ?? '/', { replace: true });
          });
        }}
      >
        <FormError error={error} />
        <Input label="อีเมล" type="email" required autoComplete="email" autoFocus
               value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <div>
          <Input label="รหัสผ่าน" type="password" required autoComplete="current-password"
                 value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <div className="mt-1.5 text-right">
            <Link to="/forgot-password" className="text-xs text-[var(--brand)] hover:underline">ลืมรหัสผ่าน?</Link>
          </div>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={busy}>เข้าสู่ระบบ</Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const { busy, error, fieldError, run } = useAuthForm();
  const [form, setForm] = useState({
    fullName: '', companyName: '', email: '', password: '', acceptTerms: false,
  });

  return (
    <AuthLayout
      title="สมัครใช้งาน"
      subtitle="ฟรี ไม่ต้องใช้บัตรเครดิต — สร้างบล็อกแรกได้ทันที"
      footer={<>มีบัญชีแล้ว? <Link to="/login" className="font-medium text-[var(--brand)] hover:underline">เข้าสู่ระบบ</Link></>}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const session = await api.post<Session>('/v1/auth/register', form, { anonymous: true });
            setSession(session);
            navigate('/', { replace: true });
          });
        }}
      >
        <FormError error={error} />
        <Input label="ชื่อ-นามสกุล" required autoFocus autoComplete="name" error={fieldError.fullName}
               value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        <Input label="ชื่อบริษัท" required autoComplete="organization" error={fieldError.companyName}
               hint="ใช้ตั้งชื่อบล็อกและ URL เริ่มต้นให้อัตโนมัติ — เปลี่ยนภายหลังได้"
               value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
        <Input label="อีเมล" type="email" required autoComplete="email" error={fieldError.email}
               value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="รหัสผ่าน" type="password" required autoComplete="new-password" minLength={10}
               error={fieldError.password} hint="อย่างน้อย 10 ตัวอักษร"
               value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />

        <label className="flex items-start gap-2.5 text-sm text-[var(--ink-2)]">
          <input type="checkbox" required className="mt-0.5 h-4 w-4 rounded border-[var(--line)] text-[var(--brand)]"
                 checked={form.acceptTerms}
                 onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })} />
          <span>ฉันยอมรับเงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว</span>
        </label>

        <Button type="submit" size="lg" className="w-full" loading={busy}>สร้างบัญชี</Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { busy, error, run } = useAuthForm();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <AuthLayout title="ตรวจกล่องจดหมายของคุณ"
                  footer={<Link to="/login" className="text-[var(--brand)] hover:underline">กลับไปหน้าเข้าสู่ระบบ</Link>}>
        <p className="text-sm text-[var(--ink-2)]">
          ถ้าอีเมล <strong>{email}</strong> มีอยู่ในระบบ เราได้ส่งลิงก์ตั้งรหัสผ่านใหม่ไปให้แล้ว
          ลิงก์ใช้ได้ภายใน 1 ชั่วโมง
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="ลืมรหัสผ่าน" subtitle="กรอกอีเมลที่ใช้สมัคร แล้วเราจะส่งลิงก์ตั้งรหัสผ่านใหม่ไปให้"
                footer={<Link to="/login" className="text-[var(--brand)] hover:underline">กลับไปหน้าเข้าสู่ระบบ</Link>}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          await api.post('/v1/auth/forgot-password', { email }, { anonymous: true });
          setSent(true);
        });
      }}>
        <FormError error={error} />
        <Input label="อีเมล" type="email" required autoFocus value={email}
               onChange={(e) => setEmail(e.target.value)} />
        <Button type="submit" size="lg" className="w-full" loading={busy}>ส่งลิงก์ตั้งรหัสผ่านใหม่</Button>
      </form>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { busy, error, fieldError, run } = useAuthForm();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const token = params.get('token') ?? '';

  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <AuthLayout title="ตั้งรหัสผ่านใหม่">
      {!token ? (
        <p className="text-sm text-[var(--bad)]">ลิงก์ไม่ถูกต้อง — ไม่พบโทเคนใน URL</p>
      ) : (
        <form className="space-y-4" onSubmit={(e) => {
          e.preventDefault();
          if (mismatch) return;
          void run(async () => {
            await api.post('/v1/auth/reset-password', { token, password }, { anonymous: true });
            navigate('/login?reset=1', { replace: true });
          });
        }}>
          <FormError error={error} />
          <Input label="รหัสผ่านใหม่" type="password" required minLength={10} autoFocus
                 autoComplete="new-password" error={fieldError.password} hint="อย่างน้อย 10 ตัวอักษร"
                 value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input label="ยืนยันรหัสผ่านใหม่" type="password" required autoComplete="new-password"
                 error={mismatch ? 'รหัสผ่านทั้งสองช่องไม่ตรงกัน' : null}
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button type="submit" size="lg" className="w-full" loading={busy} disabled={mismatch}>
            บันทึกรหัสผ่านใหม่
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [state, setState] = useState<'working' | 'ok' | 'fail'>('working');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setState('fail'); setMessage('ลิงก์ไม่ถูกต้อง — ไม่พบโทเคนใน URL'); return; }
    void (async () => {
      try {
        const session = await api.post<Session>('/v1/auth/verify-email', { token }, { anonymous: true });
        setSession(session);
        setState('ok');
        setTimeout(() => navigate('/', { replace: true }), 1400);
      } catch (err) {
        setState('fail');
        setMessage(errorMessage(err));
      }
    })();
  }, [params, navigate, setSession]);

  return (
    <AuthLayout title="ยืนยันอีเมล">
      {state === 'working' && (
        <div className="flex items-center gap-3 text-sm text-[var(--ink-3)]"><Spinner /> กำลังยืนยัน…</div>
      )}
      {state === 'ok' && (
        <p className="text-sm text-[var(--ok)]">ยืนยันอีเมลเรียบร้อย กำลังพาไปหน้าหลัก…</p>
      )}
      {state === 'fail' && (
        <>
          <p className="text-sm text-[var(--bad)]">{message}</p>
          <Link to="/login" className="mt-4 inline-block text-sm text-[var(--brand)] hover:underline">กลับไปหน้าเข้าสู่ระบบ</Link>
        </>
      )}
    </AuthLayout>
  );
}

interface InvitePreview {
  email: string; role: string; companyName: string; logoUrl: string | null;
  expiresAt: string; needsAccount: boolean; fullName: string | null;
}

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const { busy, error, run, setError } = useAuthForm();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ fullName: '', password: '' });

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<InvitePreview>(`/v1/auth/invite/${encodeURIComponent(token)}`, { anonymous: true });
        setPreview(data);
        setForm((f) => ({ ...f, fullName: data.fullName ?? '' }));
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, setError]);

  if (loading) {
    return <AuthLayout title="คำเชิญเข้าร่วมทีม"><div className="flex gap-3 text-sm text-[var(--ink-3)]"><Spinner /> กำลังตรวจสอบคำเชิญ…</div></AuthLayout>;
  }
  if (!preview) {
    return (
      <AuthLayout title="คำเชิญไม่ถูกต้อง" footer={<Link to="/login" className="text-[var(--brand)] hover:underline">ไปหน้าเข้าสู่ระบบ</Link>}>
        <FormError error={error ?? 'คำเชิญนี้หมดอายุหรือถูกยกเลิกไปแล้ว'} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={`เข้าร่วม ${preview.companyName}`}
                subtitle={`คุณได้รับเชิญในสิทธิ์ ${preview.role} สำหรับอีเมล ${preview.email}`}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          const session = await api.post<Session>('/v1/auth/invite/accept', {
            token,
            ...(preview.needsAccount ? { fullName: form.fullName, password: form.password } : {}),
          }, { anonymous: true });
          setSession(session);
          navigate('/', { replace: true });
        });
      }}>
        <FormError error={error} />
        {preview.needsAccount && (
          <>
            <Input label="ชื่อ-นามสกุล" required autoFocus value={form.fullName}
                   onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
            <Input label="ตั้งรหัสผ่าน" type="password" required minLength={10}
                   autoComplete="new-password" hint="อย่างน้อย 10 ตัวอักษร"
                   value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </>
        )}
        <Button type="submit" size="lg" className="w-full" loading={busy}>ตอบรับคำเชิญ</Button>
      </form>
    </AuthLayout>
  );
}
